import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Code, Rocket, BarChart, ChevronRight, CheckCircle, Sparkles, MessageSquare, ArrowRight, Target, Award, Brain, Shield, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChatBot from '../components/ChatBot';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState('');
  const [showChatbot, setShowChatbot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState('');
  const [experience, setExperience] = useState('beginner');
  const [xpPoints, setXpPoints] = useState(0);
  const [badges, setBadges] = useState<string[]>([]);

  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    // Otorgar puntos XP por seleccionar un rol
    setXpPoints(prev => prev + 50);
    // Otorgar insignia por completar el primer paso
    if (!badges.includes('Explorador')) {
      setBadges(prev => [...prev, 'Explorador']);
    }
  };

  const handleContinue = () => {
    if (step < 4) {
      setStep(step + 1);
      // Otorgar puntos XP por avanzar al siguiente paso
      setXpPoints(prev => prev + 25);
    } else {
      // Otorgar insignia por completar todo el onboarding
      if (!badges.includes('Visionario')) {
        setBadges(prev => [...prev, 'Visionario']);
      }
      // Finalizar onboarding y redirigir al dashboard
      setLoading(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const isStepComplete = () => {
    switch (step) {
      case 1:
        return !!selectedRole;
      case 2:
        return true; // Siempre se puede continuar en el paso de explicación
      case 3:
        return experience !== '';
      case 4:
        return goals.length > 10; // Requiere al menos algunos caracteres
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Brain className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">Cortex AI</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="mt-4 text-3xl font-extrabold text-foreground sm:text-4xl">
            Bienvenido a Cortex AI
          </h1>
          <p className="mt-3 text-xl text-foreground/80">
            Vamos a personalizar tu experiencia de aprendizaje
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground/70">Paso {step} de 4</span>
            <span className="text-sm font-medium text-foreground/70">{Math.round((step / 4) * 100)}% completado</span>
          </div>
          <div className="h-2 bg-secondary rounded-full">
            <div
              className="h-2 bg-accent rounded-full transition-all duration-500"
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Puntos XP e insignias */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center">
            <Award className="h-5 w-5 text-yellow-500 mr-2" />
            <span className="text-sm font-medium text-foreground/80">{xpPoints} XP</span>
          </div>
          <div className="flex space-x-2">
            {badges.map((badge, index) => (
              <div key={index} className="bg-accent/10 text-accent text-xs px-2 py-1 rounded-full flex items-center">
                <CheckCircle className="h-3 w-3 mr-1" />
                {badge}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="dark-card">
          {step === 1 && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Selecciona tu ruta profesional</h2>
              <p className="text-foreground/80 mb-6">
                Elige la ruta profesional que más te interese. Personalizaremos tu experiencia de aprendizaje basada en tu elección.
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <button
                  onClick={() => handleRoleSelect('product-manager')}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedRole === 'product-manager'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full ${
                      selectedRole === 'product-manager' ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                    }`}>
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Product Manager</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Aprende a liderar equipos y desarrollar productos exitosos
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('developer')}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedRole === 'developer'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full ${
                      selectedRole === 'developer' ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                    }`}>
                      <Code className="h-5 w-5" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Programador</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Desarrolla habilidades técnicas y crea aplicaciones reales
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('entrepreneur')}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedRole === 'entrepreneur'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full ${
                      selectedRole === 'entrepreneur' ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                    }`}>
                      <Rocket className="h-5 w-5" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Emprendedor</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Aprende a validar ideas y construir negocios rentables
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('digital-marketer')}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedRole === 'digital-marketer'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full ${
                      selectedRole === 'digital-marketer' ? 'bg-accent text-white' : 'bg-accent/10 text-accent'
                    }`}>
                      <BarChart className="h-5 w-5" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Marketing Digital</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Domina estrategias de adquisición y retención de clientes
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Cómo funciona Cortex AI</h2>
              <p className="text-foreground/80 mb-6">
                Entendamos cómo funciona nuestra mentoría impulsada por IA para maximizar tu aprendizaje.
              </p>

              <div className="space-y-6">
                <div className="bg-secondary/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-accent text-white">
                        1
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">Tu IA Personal</h3>
                      <p className="mt-1 text-foreground/80">
                        Tu IA te acompañará en todo tu recorrido de aprendizaje, proporcionándote orientación personalizada y apoyo constante.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-accent text-white">
                        2
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">Tareas del Mundo Real</h3>
                      <p className="mt-1 text-foreground/80">
                        Recibirás tareas diarias y semanales basadas en situaciones laborales reales en tu campo elegido, diseñadas para desarrollar habilidades prácticas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-accent text-white">
                        3
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">Feedback Personalizado</h3>
                      <p className="mt-1 text-foreground/80">
                        Tu IA evaluará tu trabajo y te proporcionará feedback detallado y constructivo para ayudarte a mejorar continuamente.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-accent text-white">
                        4
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">Simulaciones de Decisiones</h3>
                      <p className="mt-1 text-foreground/80">
                        Enfrentarás escenarios de toma de decisiones que simulan desafíos reales en tu campo profesional, con tu IA guiándote en cada paso.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-accent text-white">
                        5
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">Gamificación y Progreso</h3>
                      <p className="mt-1 text-foreground/80">
                        Ganarás XP, insignias y subirás de nivel mientras avanzas, haciendo que tu aprendizaje sea divertido y motivador.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowChatbot(true)}
                  className="inline-flex items-center text-accent hover:text-accent/90"
                >
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Hablar con tu IA para saber más
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Tu nivel de experiencia</h2>
              <p className="text-foreground/80 mb-6">
                Ayúdanos a entender tu nivel actual para personalizar mejor tu experiencia de aprendizaje.
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => setExperience('beginner')}
                  className={`w-full p-4 border rounded-lg text-left transition-all ${
                    experience === 'beginner'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-6 w-6 rounded-full border flex items-center justify-center ${
                      experience === 'beginner' ? 'border-accent bg-accent text-white' : 'border-border'
                    }`}>
                      {experience === 'beginner' && <CheckCircle className="h-4 w-4" />}
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Principiante</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Tengo poco o ningún conocimiento en este campo
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setExperience('intermediate')}
                  className={`w-full p-4 border rounded-lg text-left transition-all ${
                    experience === 'intermediate'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-6 w-6 rounded-full border flex items-center justify-center ${
                      experience === 'intermediate' ? 'border-accent bg-accent text-white' : 'border-border'
                    }`}>
                      {experience === 'intermediate' && <CheckCircle className="h-4 w-4" />}
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Intermedio</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Tengo algunos conocimientos y experiencia básica
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setExperience('advanced')}
                  className={`w-full p-4 border rounded-lg text-left transition-all ${
                    experience === 'advanced'
                      ? 'border-accent bg-accent/10'
                      : 'border-border hover:border-accent/50 hover:bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 h-6 w-6 rounded-full border flex items-center justify-center ${
                      experience === 'advanced' ? 'border-accent bg-accent text-white' : 'border-border'
                    }`}>
                      {experience === 'advanced' && <CheckCircle className="h-4 w-4" />}
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium text-foreground">Avanzado</h3>
                      <p className="text-sm text-foreground/70 mt-1">
                        Tengo experiencia significativa y busco perfeccionar mis habilidades
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">Tus objetivos profesionales</h2>
              <p className="text-foreground/80 mb-6">
                Cuéntanos sobre tus objetivos profesionales para que tu IA pueda ayudarte a alcanzarlos.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="goals" className="block text-sm font-medium text-foreground/80 mb-1">
                    ¿Qué esperas lograr con Cortex AI?
                  </label>
                  <textarea
                    id="goals"
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-secondary/30 text-foreground focus:outline-none focus:ring-accent focus:border-accent"
                    placeholder="Por ejemplo: Quiero desarrollar habilidades para conseguir mi primer trabajo como Product Manager..."
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                  ></textarea>
                </div>

                <div className="bg-accent/10 p-4 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-foreground">Consejo de tu IA</h3>
                      <p className="mt-1 text-sm text-foreground/80">
                        Cuanto más específicos sean tus objetivos, mejor podré personalizar tu experiencia de aprendizaje y ayudarte a alcanzar tus metas profesionales.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="px-6 py-4 bg-secondary/30 flex justify-between">
            {step > 1 ? (
              <button
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-background hover:bg-secondary/50"
              >
                Atrás
              </button>
            ) : (
              <div></div> // Empty div to maintain layout
            )}
            <button
              onClick={handleContinue}
              disabled={!isStepComplete() || loading}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                isStepComplete() && !loading
                  ? 'bg-accent hover:bg-accent/90'
                  : 'bg-accent/50 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Preparando tu experiencia...
                </>
              ) : step < 4 ? (
                <>
                  Continuar
                  <ChevronRight className="ml-1 h-4 w-4" />
                </>
              ) : (
                <>
                  Comenzar mi aventura
                  <ArrowRight className="ml-1 h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Resumen de selecciones (visible en todos los pasos después del primero) */}
        {step > 1 && selectedRole && (
          <div className="mt-8 dark-card p-4 rounded-lg">
            <h4 className="text-sm font-medium text-foreground/80 mb-2">Tu plan personalizado</h4>
            <div className="flex items-center mb-2">
              {selectedRole === 'product-manager' ? <Briefcase className="h-5 w-5 text-accent" /> :
               selectedRole === 'developer' ? <Code className="h-5 w-5 text-accent" /> :
               selectedRole === 'entrepreneur' ? <Rocket className="h-5 w-5 text-accent" /> :
               selectedRole === 'digital-marketer' ? <BarChart className="h-5 w-5 text-accent" /> :
               <Brain className="h-5 w-5 text-accent" />}
              <span className="ml-2 text-sm text-foreground/80">
                {selectedRole === 'product-manager' ? 'Product Manager' :
                 selectedRole === 'developer' ? 'Desarrollador' :
                 selectedRole === 'entrepreneur' ? 'Emprendedor' :
                 selectedRole === 'digital-marketer' ? 'Marketing Digital' : 'Profesional'}
              </span>
            </div>
            {experience && (
              <div className="flex items-center mb-2">
                <Shield className="h-4 w-4 text-accent" />
                <span className="ml-2 text-sm text-foreground/80">
                  Nivel: {experience === 'beginner' ? 'Principiante' : 
                          experience === 'intermediate' ? 'Intermedio' : 'Avanzado'}
                </span>
              </div>
            )}
            {step >= 4 && goals && (
              <div className="flex items-center mb-2">
                <Target className="h-4 w-4 text-accent" />
                <span className="ml-2 text-sm text-foreground/80">
                  Objetivos definidos
                </span>
              </div>
            )}
            <div className="flex items-center">
              <Zap className="h-4 w-4 text-accent" />
              <span className="ml-2 text-sm text-foreground/80">
                {xpPoints} puntos de experiencia acumulados
              </span>
            </div>
          </div>
        )}
      </div>

      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role={selectedRole || 'general'} 
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;